// Contextual help for the Organizations tab.
//
// Short, section-scoped summaries shown in the FloatingHelpPanel next to each section's
// "?" — the same non-modal panel the project setup pages use, so the user can read it
// while filling the form.
//
// These are summaries ON PURPOSE, not the guide text itself. docs/userguide/workspaces.js
// is long-form prose meant for a full-width reader; pasted into a small floating panel it
// reads badly. Each topic therefore carries a `guide` pointer so the panel can link out to
// the full section rather than duplicating it — that link is the single source of truth,
// and these blurbs stay short enough not to drift from it.
//
// COVERAGE NOTE: the membership/SSO topics below are condensed from existing guide content
// (workspaces.js: "Create an organization", "Members, roles & invites", "Roles", "Invite a
// member", "Manage members", "Organization single sign-on"). The BILLING topics have no
// counterpart in the user guide yet — they were written here from the UI's own behaviour and
// should be reviewed, then folded back into the guide so the two cannot diverge.

export const USER_GUIDE_PATH = "/help/user-guide";

// key -> { title, body: string[], guide?: "Guide section name to read in full" }
const ORGANIZATION_HELP = {
  // ---- membership / identity (condensed from workspaces.js) ----
  "org-overview": {
    title: "Organizations",
    body: [
      "An organization is a shared workspace. Projects, scenarios and credits created inside it belong to the organization rather than to you personally, so members keep access when people join or leave.",
      "Your personal workspace stays separate and private — switching workspace changes which projects you see and which balance a run is charged to.",
    ],
    guide: "Workspaces & Organizations",
  },
  "org-members": {
    title: "Members & roles",
    body: [
      "Members are people who already have an account and have accepted an invite.",
      "Org owner — full control: billing, members, SSO, and every project.",
      "Member — works in the organization's projects and spends its credits, but cannot change billing or membership.",
      "Removing a member revokes their access to organization projects immediately. It does not delete anything they created — that stays with the organization.",
    ],
    guide: "Members, roles & invites",
  },
  "org-invites": {
    title: "Pending invites",
    body: [
      "An invite is sent by email and stays pending until accepted. The recipient needs an account on the platform — they can create one with the same email address and the invite will be waiting.",
      "Pending invites do not consume a seat or cost anything until accepted.",
      "Revoke an invite if it was sent to the wrong address; the link stops working immediately.",
    ],
    guide: "Invite a member",
  },
  "org-identity": {
    title: "Single sign-on (SSO)",
    body: [
      "SSO lets members sign in with your company identity provider (Microsoft or Google) instead of a separate password.",
      "Verifying a domain tells the platform that email addresses on that domain belong to your organization, so those users can be routed to your identity provider.",
      "Setting this up does not remove existing passwords — it adds a sign-in route.",
    ],
    guide: "Organization single sign-on (SSO)",
  },

  // ---- billing (NOT yet in the user guide — see the coverage note above) ----
  "billing-payment-methods": {
    title: "Saved payment methods",
    body: [
      "Cards stored against the organization, used for both subscription renewals and one-off credit-pack purchases.",
      "Card details are held by the payment provider, not by this platform — only the last four digits and expiry are shown here.",
      "The organization needs at least one payment method before a subscription can renew.",
    ],
  },
  "billing-subscription-plans": {
    title: "Subscription plans",
    body: [
      "A plan grants the organization a recurring allowance of simulation credits at the start of each billing cycle.",
      "Changing plan takes effect from the next cycle unless stated otherwise at checkout.",
      "Unused plan credits do not roll over between cycles — buy a credit pack instead if you need capacity that persists.",
    ],
  },
  "billing-credit-packs": {
    title: "Credit packs",
    body: [
      "A one-off purchase of simulation credits, on top of whatever the plan grants.",
      "Pack credits do not expire at the end of a billing cycle, so they are the right choice for occasional or bursty use.",
      "Runs consume plan credits first and fall back to pack credits, so a pack acts as a buffer when a cycle runs out.",
    ],
  },
  "billing-status-balances": {
    title: "Status & balances",
    body: [
      "Shows the organization's current billing state and how many credits are available to spend.",
      "\"Unconfigured\" means no plan or payment method has been set up yet — members can still be invited, but runs cannot be charged to the organization.",
      "The balance shown here is what a new run will draw on; a run is estimated and reserved before it starts.",
    ],
  },
  "billing-profile": {
    title: "Billing profile",
    body: [
      "The legal name, address, tax ID and billing contact printed on the organization's invoices.",
      "Set this before the first purchase — invoices already issued keep the details that were current at the time.",
      "The billing contact receives receipts and renewal notices; it does not have to be an org owner.",
    ],
  },
  "billing-history": {
    title: "Billing history",
    body: [
      "Past invoices and receipts for this organization, available to download as PDFs.",
      "Each entry corresponds to a subscription renewal or a credit-pack purchase.",
    ],
  },
  "billing-summary": {
    title: "Subscription summary",
    body: [
      "The plan currently in effect, when the cycle renews, and what the next charge will be.",
    ],
  },

  // ---- usage ----
  "usage-dashboard": {
    title: "Usage overview",
    body: [
      "A read-only summary of the organization's usage: which budgets are capped, which are near their cap, and recent activity — without expanding each budget row.",
      "Use it to spot a member or project about to hit its limit before a run is refused.",
    ],
  },
  "usage-overview": {
    title: "Usage budgets and recent activity",
    body: [
      "Optional caps on how many credits a member or a project may spend. A cap is a guard rail, not a reservation — it limits spending but does not set credits aside.",
      "A run that would exceed a cap is refused before it starts, so nothing is charged.",
      "Leaving a budget unset means the member or project can draw on the organization's whole balance.",
    ],
  },
  "usage-ledger": {
    title: "Usage ledger",
    body: [
      "A line per credit movement — every run charged, refund issued, plan grant and pack purchase.",
      "Use it to see exactly what a scenario cost and who launched it. A refund appears as its own entry rather than editing the original charge, so the history is auditable.",
    ],
  },
  "usage-plan-credit-cycles": {
    title: "Plan credit cycles",
    body: [
      "How many credits each billing cycle granted and how much of that was spent before the cycle ended.",
      "Unused plan credits lapse at the end of their cycle — a cycle that repeatedly runs out is a sign the plan is undersized.",
    ],
  },
};

export const getOrganizationHelp = (key) => ORGANIZATION_HELP[key] || null;

export default ORGANIZATION_HELP;
